import React, { useState } from 'react';
import { Sun, Moon, Monitor, Bell, Shield, Database, Globe } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Settings: React.FC = () => {
  const { theme, setTheme } = useTheme();

  const [emailNotifications, setEmailNotifications] = useState(true);
  const [qualificationAlerts, setQualificationAlerts] = useState(true);
  const [rosterAlerts, setRosterAlerts] = useState(true);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage your application preferences
        </p>
      </div>

      {/* Appearance Settings */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/10">
            <Sun className="h-5 w-5 text-sky-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Appearance</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Customize how the app looks</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
              Theme
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Dark Theme Option */}
              <button
                onClick={() => setTheme('dark')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  theme === 'dark'
                    ? 'border-sky-500 bg-sky-500/10'
                    : 'border-gray-700 dark:border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-900 border border-gray-700">
                    <Moon className="h-6 w-6 text-sky-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">Dark</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Easy on the eyes</p>
                  </div>
                </div>
                {theme === 'dark' && (
                  <div className="mt-3 text-xs text-sky-400 font-medium">Currently Active</div>
                )}
              </button>

              {/* Light Theme Option */}
              <button
                onClick={() => setTheme('light')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  theme === 'light'
                    ? 'border-sky-500 bg-sky-500/10'
                    : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white border border-gray-200">
                    <Sun className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">Light</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Classic bright look</p>
                  </div>
                </div>
                {theme === 'light' && (
                  <div className="mt-3 text-xs text-sky-400 font-medium">Currently Active</div>
                )}
              </button>

              {/* System Theme Option (disabled for now) */}
              <button
                disabled
                className="p-4 rounded-xl border-2 border-gray-300 dark:border-gray-700 opacity-50 cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-gray-100 to-gray-900 border border-gray-400">
                    <Monitor className="h-6 w-6 text-gray-500" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">System</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Coming soon</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications Settings */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/10">
            <Bell className="h-5 w-5 text-sky-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Manage notification preferences</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-800">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Email Notifications</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Receive updates via email</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:ring-2 peer-focus:ring-sky-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-500"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-800">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Qualification Expiry Alerts</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Get notified before qualifications expire</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={qualificationAlerts}
                onChange={(e) => setQualificationAlerts(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:ring-2 peer-focus:ring-sky-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-500"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Roster Change Alerts</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Notify when roster is updated</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={rosterAlerts}
                onChange={(e) => setRosterAlerts(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:ring-2 peer-focus:ring-sky-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-500"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Other Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Security */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/10">
              <Shield className="h-5 w-5 text-sky-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Security</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Account security settings</p>
            </div>
          </div>
          <button
            onClick={() => alert('Security settings coming soon')}
            className="btn-secondary w-full"
          >
            Manage Security
          </button>
        </div>

        {/* Data */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/10">
              <Database className="h-5 w-5 text-sky-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Data</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Export and manage data</p>
            </div>
          </div>
          <button
            onClick={() => alert('Data export coming soon')}
            className="btn-secondary w-full"
          >
            Export Data
          </button>
        </div>
      </div>

      {/* Version Info */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/10">
            <Globe className="h-5 w-5 text-sky-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">About</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">CREW Module v1.0.0 - FlightAtom</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
